import { FC, useEffect, useState } from 'react'
import { ModalSheet } from '@/atoms/ModalSheet'
import { UNITED_STATES } from '@/constants/regions'
import { useTheatricalRegionContext } from '@/contexts/TheatricalRegionContext'
import { TheaterPreferencesProvider, useTheaterPreferences } from './TheaterPreferencesContext'
import { ExistingPreferencesStep } from './steps/ExistingPreferencesStep'
import { TheaterSelectionStep } from './steps/TheaterSelectionStep'
import { PreferenceStep, TheaterPreferencesModalProps } from './types'

interface TheaterPreferencesContentProps {
  onClose: () => void
}

const TheaterPreferencesContent: FC<TheaterPreferencesContentProps> = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState<PreferenceStep>(PreferenceStep.THEATER_SELECTION)
  const { hasExistingPreferences } = useTheaterPreferences()
  const { userCountry } = useTheatricalRegionContext()
  const effectiveCountryCode = userCountry || UNITED_STATES

  useEffect(() => {
    if (hasExistingPreferences) {
      setCurrentStep(PreferenceStep.VIEW_EXISTING_PREFERENCES)
    }
  }, [hasExistingPreferences])

  const renderCurrentStep = () => {
    switch (currentStep) {
      case PreferenceStep.THEATER_SELECTION:
        return (
          <TheaterSelectionStep
            effectiveCountryCode={effectiveCountryCode}
            onNextStep={() => setCurrentStep(PreferenceStep.VIEW_EXISTING_PREFERENCES)}
            onClose={onClose}
          />
        )
      case PreferenceStep.VIEW_EXISTING_PREFERENCES:
        return (
          <ExistingPreferencesStep
            onEditTheater={() => setCurrentStep(PreferenceStep.THEATER_SELECTION)}
            onClose={onClose}
          />
        )
      default:
        return null
    }
  }

  return <div className="h-full">{renderCurrentStep()}</div>
}

// Main modal component that provides the context
export const TheaterPreferencesModal: FC<TheaterPreferencesModalProps> = ({ isOpen, onClose }) => {
  return (
    <ModalSheet
      isOpen={isOpen}
      onClose={onClose}
      panelClasses="w-full max-w-lg h-[700px] overflow-hidden"
      backgroundScreenColor="black"
    >
      <TheaterPreferencesProvider>
        <div className="flex h-full flex-col">
          <TheaterPreferencesContent onClose={onClose} />
        </div>
      </TheaterPreferencesProvider>
    </ModalSheet>
  )
}
